import React, { useState, useEffect, Suspense } from 'react';
import PersonIcon from '@mui/icons-material/Person'; // Keep as fallback

// Icon loading cache to avoid re-importing
const iconCache = new Map();

// Available icon names (metadata only - no imports)
export const AVAILABLE_ICONS = [
  'AccessAlarm', 'AccessTime', 'AccountBalance', 'AccountBox', 'AccountCircle',
  'AdminPanelSettings', 'Apartment', 'Assignment', 'AssignmentInd', 'Badge',
  'Block', 'Business', 'BusinessCenter', 'Campaign', 'Category', 'Checklist',
  'Cloud', 'Code', 'Computer', 'ContactMail', 'CorporateFare', 'Dashboard',
  'DataUsage', 'DesktopMac', 'DeveloperBoard', 'Devices', 'Domain', 'Engineering',
  'Event', 'Explore', 'Extension', 'Face', 'Feedback', 'Folder',
  'Group', 'Groups', 'GroupWork', 'Home', 'Hub', 'ImportantDevices',
  'Inventory', 'Label', 'Laptop', 'Layers', 'Leaderboard', 'LocationCity',
  'Lock', 'ManageAccounts', 'ManageHistory', 'Memory', 'Monitor',
  'NetworkCheck', 'People', 'Person', 'PersonAdd', 'Phone', 'Place',
  'PrecisionManufacturing', 'Public', 'Report', 'RocketLaunch', 'School',
  'Security', 'Settings', 'Speed', 'Star', 'Storage', 'SupervisorAccount',
  'SupportAgent', 'Traffic', 'TrendingUp', 'VerifiedUser', 'ViewModule', 'Work', 'Workspaces'
];

// Keywords for search functionality
export const ICON_KEYWORDS = {
  'AccessAlarm': ['alarm', 'time', 'clock', 'schedule'],
  'AccessTime': ['time', 'clock', 'schedule', 'timing'],
  'AccountBalance': ['bank', 'finance', 'money', 'balance'],
  'AccountBox': ['account', 'profile', 'user', 'person'],
  'AccountCircle': ['account', 'profile', 'user', 'person', 'avatar'],
  'AdminPanelSettings': ['admin', 'settings', 'management', 'control', 'administrator'],
  'Apartment': ['building', 'office', 'company', 'organization', 'apartment'],
  'Assignment': ['task', 'assignment', 'document', 'file', 'work'],
  'AssignmentInd': ['assignment', 'person', 'individual', 'task', 'user'],
  'Badge': ['badge', 'id', 'identification', 'card', 'employee'],
  'Business': ['business', 'company', 'building', 'office', 'corporate'],
  'BusinessCenter': ['briefcase', 'business', 'work', 'professional', 'job'],
  'Campaign': ['megaphone', 'announcement', 'marketing', 'campaign', 'promotion'],
  'Category': ['category', 'grid', 'apps', 'menu', 'organize'],
  'Checklist': ['checklist', 'list', 'tasks', 'todo', 'items', 'check'],
  'Cloud': ['cloud', 'storage', 'online', 'server', 'data'],
  'Code': ['code', 'programming', 'development', 'coding', 'developer'],
  'Computer': ['computer', 'desktop', 'pc', 'technology', 'tech'],
  'ContactMail': ['contact', 'mail', 'email', 'communication', 'message'],
  'CorporateFare': ['corporate', 'building', 'business', 'company', 'office'],
  'Dashboard': ['dashboard', 'control', 'panel', 'overview', 'summary'],
  'DataUsage': ['data', 'usage', 'analytics', 'chart', 'statistics'],
  'DesktopMac': ['desktop', 'mac', 'computer', 'monitor', 'screen'],
  'DeveloperBoard': ['developer', 'board', 'circuit', 'tech', 'hardware'],
  'Devices': ['devices', 'technology', 'gadgets', 'electronics', 'tech'],
  'Domain': ['domain', 'building', 'organization', 'company', 'business'],
  'Engineering': ['engineering', 'technical', 'engineer', 'gear', 'mechanical'],
  'Event': ['event', 'calendar', 'schedule', 'date', 'meeting'],
  'Explore': ['explore', 'compass', 'navigation', 'discover', 'search'],
  'Extension': ['extension', 'plugin', 'puzzle', 'addon', 'module'],
  'Face': ['face', 'person', 'user', 'avatar', 'profile'],
  'Feedback': ['feedback', 'comment', 'review', 'opinion', 'input'],
  'Folder': ['folder', 'directory', 'file', 'storage', 'organize'],
  'Group': ['group', 'team', 'people', 'users', 'collaboration'],
  'Groups': ['groups', 'teams', 'people', 'users', 'multiple'],
  'GroupWork': ['group', 'work', 'collaboration', 'team', 'together'],
  'Home': ['home', 'house', 'main', 'start', 'dashboard'],
  'Hub': ['hub', 'network', 'connection', 'center', 'node'],
  'ImportantDevices': ['devices', 'important', 'priority', 'technology', 'critical'],
  'Inventory': ['inventory', 'list', 'stock', 'items', 'manage'],
  'Label': ['label', 'tag', 'category', 'mark', 'organize'],
  'Laptop': ['laptop', 'computer', 'portable', 'device', 'work'],
  'Layers': ['layers', 'stack', 'level', 'hierarchy', 'organize'],
  'Leaderboard': ['leaderboard', 'ranking', 'chart', 'performance', 'top'],
  'LocationCity': ['city', 'location', 'place', 'urban', 'building'],
  'Lock': ['lock', 'security', 'private', 'secure', 'protected'],
  'ManageAccounts': ['manage', 'accounts', 'users', 'admin', 'control'],
  'ManageHistory': ['manage', 'history', 'time', 'track', 'record'],
  'Memory': ['memory', 'chip', 'hardware', 'tech', 'computer'],
  'Monitor': ['monitor', 'screen', 'display', 'computer', 'view'],
  'NetworkCheck': ['network', 'check', 'connection', 'status', 'online'],
  'People': ['people', 'users', 'group', 'team', 'community'],
  'Person': ['person', 'user', 'individual', 'profile', 'account'],
  'PersonAdd': ['person', 'add', 'new', 'user', 'create'],
  'Phone': ['phone', 'call', 'contact', 'communication', 'mobile'],
  'Place': ['place', 'location', 'pin', 'marker', 'map'],
  'PrecisionManufacturing': ['manufacturing', 'precision', 'factory', 'production', 'industrial'],
  'Public': ['public', 'world', 'global', 'open', 'shared'],
  'Report': ['report', 'document', 'analysis', 'data', 'summary'],
  'RocketLaunch': ['rocket', 'launch', 'startup', 'fast', 'innovation'],
  'School': ['school', 'education', 'learning', 'student', 'academic'],
  'Security': ['security', 'shield', 'protection', 'safe', 'secure'],
  'Settings': ['settings', 'config', 'configuration', 'options', 'preferences'],
  'Speed': ['speed', 'fast', 'performance', 'quick', 'velocity'],
  'Star': ['star', 'favorite', 'rating', 'important', 'highlight'],
  'Storage': ['storage', 'database', 'data', 'save', 'repository'],
  'SupervisorAccount': ['supervisor', 'manager', 'lead', 'admin', 'oversight'],
  'SupportAgent': ['support', 'agent', 'help', 'assistance', 'service'],
  'TrendingUp': ['trending', 'up', 'growth', 'increase', 'progress'],
  'VerifiedUser': ['verified', 'user', 'check', 'approved', 'trusted'],
  'ViewModule': ['view', 'module', 'grid', 'layout', 'organize'],
  'Work': ['work', 'job', 'briefcase', 'business', 'professional'],
  'Workspaces': ['workspaces', 'multiple', 'areas', 'spaces', 'organize']
};

// Create icon import mapping for dynamic loading
const createIconImportMap = () => {
  const importMap = {};
  
  // Map each icon name to its import function
  importMap['AccessAlarm'] = () => import('@mui/icons-material/AccessAlarm');
  importMap['AccessTime'] = () => import('@mui/icons-material/AccessTime');
  importMap['AccountBalance'] = () => import('@mui/icons-material/AccountBalance');
  importMap['AccountBox'] = () => import('@mui/icons-material/AccountBox');
  importMap['AccountCircle'] = () => import('@mui/icons-material/AccountCircle');
  importMap['AdminPanelSettings'] = () => import('@mui/icons-material/AdminPanelSettings');
  importMap['Apartment'] = () => import('@mui/icons-material/Apartment');
  importMap['Assignment'] = () => import('@mui/icons-material/Assignment');
  importMap['AssignmentInd'] = () => import('@mui/icons-material/AssignmentInd');
  importMap['Badge'] = () => import('@mui/icons-material/Badge');
  importMap['Block'] = () => import('@mui/icons-material/Block');
  importMap['Business'] = () => import('@mui/icons-material/Business');
  importMap['BusinessCenter'] = () => import('@mui/icons-material/BusinessCenter');
  importMap['Campaign'] = () => import('@mui/icons-material/Campaign');
  importMap['Category'] = () => import('@mui/icons-material/Category');
  importMap['Checklist'] = () => import('@mui/icons-material/Checklist');
  importMap['Cloud'] = () => import('@mui/icons-material/Cloud');
  importMap['Code'] = () => import('@mui/icons-material/Code');
  importMap['Computer'] = () => import('@mui/icons-material/Computer');
  importMap['ContactMail'] = () => import('@mui/icons-material/ContactMail');
  importMap['CorporateFare'] = () => import('@mui/icons-material/CorporateFare');
  importMap['Dashboard'] = () => import('@mui/icons-material/Dashboard');
  importMap['DataUsage'] = () => import('@mui/icons-material/DataUsage');
  importMap['DesktopMac'] = () => import('@mui/icons-material/DesktopMac');
  importMap['DeveloperBoard'] = () => import('@mui/icons-material/DeveloperBoard');
  importMap['Devices'] = () => import('@mui/icons-material/Devices');
  importMap['Domain'] = () => import('@mui/icons-material/Domain');
  importMap['Engineering'] = () => import('@mui/icons-material/Engineering');
  importMap['Event'] = () => import('@mui/icons-material/Event');
  importMap['Explore'] = () => import('@mui/icons-material/Explore');
  importMap['Extension'] = () => import('@mui/icons-material/Extension');
  importMap['Face'] = () => import('@mui/icons-material/Face');
  importMap['Feedback'] = () => import('@mui/icons-material/Feedback');
  importMap['Folder'] = () => import('@mui/icons-material/Folder');
  importMap['Group'] = () => import('@mui/icons-material/Group');
  importMap['Groups'] = () => import('@mui/icons-material/Groups');
  importMap['GroupWork'] = () => import('@mui/icons-material/GroupWork');
  importMap['Home'] = () => import('@mui/icons-material/Home');
  importMap['Hub'] = () => import('@mui/icons-material/Hub');
  importMap['ImportantDevices'] = () => import('@mui/icons-material/ImportantDevices');
  importMap['Inventory'] = () => import('@mui/icons-material/Inventory');
  importMap['Label'] = () => import('@mui/icons-material/Label');
  importMap['Laptop'] = () => import('@mui/icons-material/Laptop');
  importMap['Layers'] = () => import('@mui/icons-material/Layers');
  importMap['Leaderboard'] = () => import('@mui/icons-material/Leaderboard');
  importMap['LocationCity'] = () => import('@mui/icons-material/LocationCity');
  importMap['Lock'] = () => import('@mui/icons-material/Lock');
  importMap['ManageAccounts'] = () => import('@mui/icons-material/ManageAccounts');
  importMap['ManageHistory'] = () => import('@mui/icons-material/ManageHistory');
  importMap['Memory'] = () => import('@mui/icons-material/Memory');
  importMap['Monitor'] = () => import('@mui/icons-material/Monitor');
  importMap['NetworkCheck'] = () => import('@mui/icons-material/NetworkCheck');
  importMap['People'] = () => import('@mui/icons-material/People');
  importMap['Person'] = () => import('@mui/icons-material/Person');
  importMap['PersonAdd'] = () => import('@mui/icons-material/PersonAdd');
  importMap['Phone'] = () => import('@mui/icons-material/Phone');
  importMap['Place'] = () => import('@mui/icons-material/Place');
  importMap['PrecisionManufacturing'] = () => import('@mui/icons-material/PrecisionManufacturing');
  importMap['Public'] = () => import('@mui/icons-material/Public');
  importMap['Report'] = () => import('@mui/icons-material/Report');
  importMap['RocketLaunch'] = () => import('@mui/icons-material/RocketLaunch');
  importMap['School'] = () => import('@mui/icons-material/School');
  importMap['Security'] = () => import('@mui/icons-material/Security');
  importMap['Settings'] = () => import('@mui/icons-material/Settings');
  importMap['Speed'] = () => import('@mui/icons-material/Speed');
  importMap['Star'] = () => import('@mui/icons-material/Star');
  importMap['Storage'] = () => import('@mui/icons-material/Storage');
  importMap['SupervisorAccount'] = () => import('@mui/icons-material/SupervisorAccount');
  importMap['SupportAgent'] = () => import('@mui/icons-material/SupportAgent');
  importMap['Traffic'] = () => import('@mui/icons-material/Traffic');
  importMap['TrendingUp'] = () => import('@mui/icons-material/TrendingUp');
  importMap['VerifiedUser'] = () => import('@mui/icons-material/VerifiedUser');
  importMap['ViewModule'] = () => import('@mui/icons-material/ViewModule');
  importMap['Work'] = () => import('@mui/icons-material/Work');
  importMap['Workspaces'] = () => import('@mui/icons-material/Workspaces');
  
  return importMap;
};

const iconImportMap = createIconImportMap();

// Dynamic icon loading function
const loadIcon = async (iconName) => {
  // Check cache first
  if (iconCache.has(iconName)) {
    return iconCache.get(iconName);
  }

  try {
    // Use the import map for dynamic loading
    if (iconImportMap[iconName]) {
      const iconModule = await iconImportMap[iconName]();
      const IconComponent = iconModule.default;
      
      // Cache the result
      iconCache.set(iconName, IconComponent);
      return IconComponent;
    } else {
      throw new Error(`Icon ${iconName} not found in import map`);
    }
  } catch (error) {
    console.warn(`Failed to load icon: ${iconName}`, error);
    // Cache the fallback to avoid repeated failed attempts
    iconCache.set(iconName, PersonIcon);
    return PersonIcon;
  }
};

// React component for dynamic icon loading
export const DynamicIcon = ({ iconName, showFallback = true, ...props }) => {
  const [IconComponent, setIconComponent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadIconComponent = async () => {
      try {
        const Icon = await loadIcon(iconName);
        if (isMounted) {
          setIconComponent(() => Icon);
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          setIconComponent(() => PersonIcon);
          setIsLoading(false);
        }
      }
    };

    loadIconComponent();

    return () => {
      isMounted = false;
    };
  }, [iconName]);

  if (isLoading) {
    // Don't show anything while loading if showFallback is false
    if (!showFallback) {
      return null;
    }
    return <PersonIcon {...props} />;
  }

  if (!IconComponent) {
    return showFallback ? <PersonIcon {...props} /> : null;
  }

  return <IconComponent {...props} />;
};

// Preload all icons for IconBrowser
export const preloadAllIcons = async () => {
  const loadPromises = AVAILABLE_ICONS.map(iconName => loadIcon(iconName));
  await Promise.all(loadPromises);
};

// Get preloaded icon component synchronously (for IconBrowser)
export const getPreloadedIcon = (iconName) => {
  if (iconCache.has(iconName)) {
    return iconCache.get(iconName);
  }
  return PersonIcon;
};

// Utility function to get role icon component (now returns DynamicIcon)
export const getRoleIcon = (iconName) => {
  if (!iconName) return <PersonIcon />;
  return <DynamicIcon iconName={iconName} />;
};